import React, { useState, useRef, useEffect } from "react";
import * as yup from "yup";
import {
  Input,
  Button,
  Typography,
  Textarea,
  Select,
  Option,
  Spinner,
} from "@material-tailwind/react";
import MultiSelect from "../../components/MultiSelect";
import SelectWithAdd from "../../components/SelectWithAdd";
import Editor from "../../components/Lexcical/Lexcical";
import CreateMultiGallery from "../../components/Property/CreateMultiGallery";
import { useNavigate } from "react-router-dom";
import DOMPurify from "dompurify";
import {
  Dialog,
  DialogBody,
  DialogHeader,
  DialogFooter,
} from "@material-tailwind/react";
import { useDispatch, useSelector } from "react-redux";
import {
  createProperty,
  getLocations,
  getTags,
} from "../../store/propertyReducers";

const CreatePropertyForm = () => {
  const [options, setOptions] = useState([]);
  const [locations, setLocations] = useState([]);
  const [description, setDescription] = useState();
  const [nearLocation, setNearLocation] = useState();
  const [treeList, setTreeList] = useState();
  const [fetching, setFetching] = useState(true);
  const formDataRef = useRef(new FormData());
  const [formData, setFormData] = useState({
    title: "",
    subtitle: "",
    location: "",
    price: 0,
    pricePerSquareWah: 0,
    pricePerRai: 0,
    finalPrice: 0,
    tags: [],
    areaSquareWah: 0,
    areaRai: 0,
  });

  const [isValid, setIsValid] = useState({
    title: true,
    location: true,
    price: true,
    areaSquareWah: true,
  });

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const loading = useSelector((state) => state.property.loading.properties);
  const propertyError = useSelector((state) => state.property.error.properties);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isResultModalOpen, setIsResultModalOpen] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    const loadTags = async () => {
      try {
        const response = await dispatch(getTags()).unwrap();
        const tags = response.map((tag) => ({
          value: tag.tag,
          count: tag.count,
        }));
        setOptions(tags);
      } catch (error) {
        console.error("Error fetching tags:", error);
      }
    };
    const loadLocations = async () => {
      try {
        const response = await dispatch(getLocations()).unwrap();
        const locations = response.map((location) => ({
          value: location.name,
          count: location.count,
        }));
        setLocations(locations);
      } catch (error) {
        console.error("Error fetching locations:", error);
      } finally {
        setFetching(false);
      }
    };
    loadLocations();
    loadTags();
  }, [dispatch]);

  function sanitizeHTML(input) {
    return DOMPurify.sanitize(input);
  }

  function formatNumberWithCommas(value) {
    if (value === null || value === "" || value === 0) return "";
    return Number(value).toLocaleString("en-US");
  }

  const onKeyDownNumber = (e) => {
    const validKeys = /^[0-9]$/;
    if (
      !validKeys.test(e.key) &&
      e.key !== "Backspace" &&
      e.key !== "ArrowLeft" &&
      e.key !== "ArrowRight" &&
      e.key !== "Tab"
    ) {
      e.preventDefault();
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    console.log("name: value", name, value);
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));

    validateField(name, value).then((res) => {
      setIsValid((prevError) => ({
        ...prevError,
        [name]: res.isValid,
      }));
    });
  };

  const handleNumberChange = (e) => {
    const { name, value } = e.target;
    const MAX_VALUE = 9999999999; // Set your maximum value here
    let rawValue = value;
    if (rawValue.startsWith("0")) {
      rawValue = rawValue.replace(/^0+/, ""); // Remove leading zeros
      window.requestAnimationFrame(() => {
        e.target.setSelectionRange(0, 0);
      });
    }
    let numericValue = rawValue === "" ? 0 : Number(rawValue);
    if (numericValue > MAX_VALUE) {
      return;
    }
    if (!isNaN(numericValue)) {
      console.log("numericValue", numericValue);
      setFormData((prevData) => ({
        ...prevData,
        [name]: numericValue,
      }));

      validateField(name, numericValue).then((res) => {
        setIsValid((prevError) => ({
          ...prevError,
          [name]: res.isValid,
        }));
      });
    }
  };

  const handlePriceChange = (e) => {
    const MAX_VALUE = 9999999999; // Set your maximum value here
    const input = e.target;
    let rawValue = input.value.replace(/,/g, ""); // Remove commas
    const cursorPosition = input.selectionStart; // Track cursor position
    const textBeforeCursor = input.value.slice(0, cursorPosition); // Text before cursor
    const commasBeforeCursor = (textBeforeCursor.match(/,/g) || []).length; // Count commas before cursor

    const numericValue = rawValue === "" ? 0 : Number(rawValue);
    // Prevent leading zeros
    if (rawValue.startsWith("0")) {
      rawValue = rawValue.replace(/^0+/, ""); // Remove leading zeros
      setFormData((prevData) => ({
        ...prevData,
        [input.name]: Number(rawValue),
      }));

      validateField(input.name, Number(rawValue)).then((res) => {
        setIsValid((prevError) => ({
          ...prevError,
          [input.name]: res.isValid,
        }));
      });
      window.requestAnimationFrame(() => {
        input.setSelectionRange(0, 0);
      });
    } else if (!isNaN(numericValue)) {
      if (numericValue > MAX_VALUE) {
        return;
      }
      setFormData((prevData) => ({
        ...prevData,
        [input.name]: numericValue,
      }));

      validateField(input.name, numericValue).then((res) => {
        setIsValid((prevError) => ({
          ...prevError,
          [input.name]: res.isValid,
        }));
      });

      // Format the value with commas
      const formattedValue = Number(numericValue).toLocaleString("en-US");

      // Calculate the new cursor position
      const newCursorPosition = cursorPosition - commasBeforeCursor;
      const newTextBeforeCursor = formattedValue.slice(0, newCursorPosition);
      const newCommasBeforeCursor = (newTextBeforeCursor.match(/,/g) || [])
        .length;
      const adjustedCursorPosition = newCursorPosition + newCommasBeforeCursor;

      // Restore the cursor position
      window.requestAnimationFrame(() => {
        input.setSelectionRange(adjustedCursorPosition, adjustedCursorPosition);
      });
    }
  };

  const schema = yup.object().shape({
    title: yup.string().required("Title is required"),
    subtitle: yup.string(),
    location: yup.string().required("Location is required"),
    price: yup
      .number()
      .required("Price is required")
      .moreThan(0, "Price must be greater than 0"),
    pricePerSquareWah: yup
      .number()
      .min(0, "Price Per Square Wah must be greater than 0"),
    pricePerRai: yup.number().min(0, "Price Per Rai must be greater than 0"),
    finalPrice: yup.number().min(0, "Final Price must be greater than 0"),
    tags: yup.array().min(0, "Tags is required"),
    areaSquareWah: yup
      .number()
      .min(0, "Area (Square Wah) must be greater than 0")
      .max(399, "Area (Square Wah) must be less than 400"),
    areaRai: yup.number().min(0, "Area (Rai) must be greater than 0"),
    description: yup
      .string()
      .test("is-html", "Description must be valid HTML", (value) => {
        if (!value) return true;
        try {
          const doc = new DOMParser().parseFromString(value, "text/html");
          return Array.from(doc.body.childNodes).some((node) => {
            return node.nodeType === 1;
          });
        } catch (error) {
          console.log("Error parsing HTML:", error);
          return false;
        }
      }),
    nearLocation: yup
      .string()
      .test("is-html", "Nearest Location must be valid HTML", (value) => {
        if (!value) return true;
        try {
          const doc = new DOMParser().parseFromString(value, "text/html");
          return Array.from(doc.body.childNodes).some(
            (node) => node.nodeType === 1
          );
        } catch (error) {
          console.log("Error parsing HTML:", error);
          return false;
        }
      }),
    treeList: yup
      .string()
      .test("is-html", "Tree List must be valid HTML", (value) => {
        if (!value) return true;
        const doc = new DOMParser().parseFromString(value, "text/html");
        return Array.from(doc.body.childNodes).some(
          (node) => node.nodeType === 1
        );
      }),
  });

  async function validateForm(dataObject) {
    try {
      await schema.validate(dataObject, { abortEarly: false });
      setIsValid({
        title: true,
        location: true,
        price: true,
        areaSquareWah: true,
      });
      return { isValid: true };
    } catch (validationErrors) {
      const errors = validationErrors.inner.reduce((acc, error) => {
        acc[error.path] = error.message;
        console.log("error", error.path, error.message);
        return acc;
      }, {});
      setIsValid((prevError) => ({
        ...prevError,
        ...Object.keys(errors).reduce((acc, key) => {
          acc[key] = false;
          return acc;
        }, {}),
      }));
      return { isValid: false };
    }
  }

  const validateField = async (fieldName, value) => {
    try {
      await yup.reach(schema, fieldName).validate(value);
      return { isValid: true };
    } catch (validationError) {
      return { isValid: false };
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const createData = {
      ...formData,
      description: description,
      nearLocation: nearLocation,
      treeList: treeList,
    };
    const validation = await validateForm(createData);
    if (!validation.isValid) {
      setIsModalOpen(false);
      setTimeout(() => {
        window.scrollTo(0, 0);
      }, 800);
      return;
    }
    console.log("submit", createData);
    formDataRef.current.set("title", JSON.stringify(createData.title));
    formDataRef.current.set("subtitle", JSON.stringify(createData.subtitle));
    formDataRef.current.set("location", JSON.stringify(createData.location));
    formDataRef.current.set("price", JSON.stringify(createData.price));
    formDataRef.current.set(
      "pricePerSquareWah",
      JSON.stringify(createData.pricePerSquareWah)
    );
    formDataRef.current.set(
      "pricePerRai",
      JSON.stringify(createData.pricePerRai)
    );
    formDataRef.current.set(
      "areaSquareWah",
      JSON.stringify(createData.areaSquareWah)
    );
    formDataRef.current.set("areaRai", JSON.stringify(createData.areaRai));
    formDataRef.current.set(
      "finalPrice",
      JSON.stringify(createData.finalPrice)
    );
    formDataRef.current.set("description", JSON.stringify(description || ""));
    formDataRef.current.set("nearLocation", JSON.stringify(nearLocation || ""));
    formDataRef.current.set("treeList", JSON.stringify(treeList || ""));

    // set tags
    if (Array.isArray(createData.tags) && createData.tags.length > 0) {
      formDataRef.current.set("tags", JSON.stringify(createData.tags));
    } else {
      formDataRef.current.set("tags", JSON.stringify([]));
    }

    // Dispatch the createProperty action
    await dispatch(createProperty(formDataRef.current))
      .unwrap()
      .then(() => {
        setIsModalOpen(false);
        setIsResultModalOpen(true);
      })
      .catch((error) => {
        setError(error.errors);
        setIsModalOpen(false);
        setIsResultModalOpen(true);
      });
  };

  return (
    <div className="w-full flex flex-col gap-y-6">
      <Typography
        variant="h4"
        color="blue-gray"
        className="text-base md:text-xl lg:text-2xl"
      >
        สร้างที่ดินใหม่
      </Typography>
      <CreateMultiGallery formData={formDataRef} />
      <form className="mb-2 px-2 lg:px-8 w-full">
        <div className="grid lg:grid-cols-4 gap-6 pb-4">
          <div className="col-span-3 flex flex-col gap-6">
            <Typography variant="h6" color="blue-gray" className="-mb-3">
              ชื่อ <span className="text-red-500">*</span>
            </Typography>
            <Input
              size={window.innerWidth < 720 ? "md" : "lg"}
              name="title"
              placeholder={isValid.title ? "ชื่อ" : "จำเป็นต้องใส่ชื่อ"}
              value={formData.title}
              onChange={handleChange}
              className={`bg-white/70 ${
                isValid.title
                  ? "text-blue-gray-700 !border-t-blue-gray-200 placeholder:text-blue-gray-700 placeholder:opacity-100 placeholder-shown:border placeholder-shown:border-blue-gray-200 placeholder-shown:border-t-blue-gray-200 focus:!border-2 focus:border-t-transparent focus:placeholder:opacity-100 focus:!border-gray-900 focus:!border-t-gray-900"
                  : "!border-red-500 placeholder:text-red-500 placeholder:opacity-100 placeholder-shown:border placeholder-shown:border-red-500 placeholder-shown:border-t-red-500 focus:!border-2 focus:placeholder:opacity-100 focus:!border-red-900 focus:!border-t-red-900"
              }`}
              containerProps={{ className: "min-w-0" }}
              labelProps={{
                className: "hidden",
              }}
              error={!isValid.title}
            />
            <Typography variant="h6" color="blue-gray" className="-mb-3">
              เนื้อหา
            </Typography>
            <Textarea
              rows={6}
              placeholder="เนื้อหา"
              name="subtitle"
              value={formData.subtitle}
              onChange={handleChange}
              className="bg-white/70 appearance-none [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none text-blue-gray-700 !border-t-blue-gray-200 placeholder:text-blue-gray-700 placeholder:opacity-100 placeholder-shown:border placeholder-shown:border-blue-gray-200 placeholder-shown:border-t-blue-gray-200 focus:!border-2 focus:border-t-transparent focus:placeholder:opacity-100 focus:!border-gray-900 focus:!border-t-gray-900"
              containerProps={{ className: "min-w-0" }}
              labelProps={{
                className: "hidden",
              }}
            />
            <div className="flex flex-row gap-3">
              <div className="flex flex-col w-full">
                <Typography variant="h6" color="blue-gray" className="mb-3">
                  สถานที่ <span className="text-red-500">*</span>
                </Typography>
                <SelectWithAdd
                  name="สถานที่"
                  options={locations}
                  value={formData.location}
                  onChange={(value) =>
                    setFormData((prevData) => ({
                      ...prevData,
                      location: value,
                    }))
                  }
                  placeholder={
                    isValid.location ? "เลือกสถานที่" : "จำเป็นต้องเลือกสถานที่"
                  }
                  label="สถานที่"
                  error={!isValid.location}
                ></SelectWithAdd>
              </div>
            </div>
          </div>
          <div className="col-span-1 flex flex-col gap-6">
            <Typography variant="h6" color="blue-gray" className="-mb-3">
              ราคาตารางวาละ (บาท)
            </Typography>
            <Input
              type="text"
              size={window.innerWidth < 720 ? "md" : "lg"}
              placeholder="ตารางวาละ"
              name="pricePerSquareWah"
              value={formatNumberWithCommas(formData.pricePerSquareWah)}
              onChange={handlePriceChange}
              onKeyDown={onKeyDownNumber}
              className="bg-white/70 appearance-none [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none text-blue-gray-700 !border-t-blue-gray-200 placeholder:text-blue-gray-700 placeholder:opacity-100 placeholder-shown:border placeholder-shown:border-blue-gray-200 placeholder-shown:border-t-blue-gray-200 focus:!border-2 focus:border-t-transparent focus:placeholder:opacity-100 focus:!border-gray-900 focus:!border-t-gray-900"
              containerProps={{ className: "min-w-0" }}
              labelProps={{
                className: "hidden",
              }}
            />
            <Typography variant="h6" color="blue-gray" className="-mb-3">
              ราคาไร่ละ (บาท)
            </Typography>
            <Input
              type="text"
              size={window.innerWidth < 720 ? "md" : "lg"}
              placeholder="ราคาไร่ละ"
              name="pricePerRai"
              value={formatNumberWithCommas(formData.pricePerRai)}
              onChange={handlePriceChange}
              onKeyDown={onKeyDownNumber}
              className="bg-white/70 appearance-none [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none text-blue-gray-700 !border-t-blue-gray-200 placeholder:text-blue-gray-700 placeholder:opacity-100 placeholder-shown:border placeholder-shown:border-blue-gray-200 placeholder-shown:border-t-blue-gray-200 focus:!border-2 focus:border-t-transparent focus:placeholder:opacity-100 focus:!border-gray-900 focus:!border-t-gray-900"
              containerProps={{ className: "min-w-0" }}
              labelProps={{
                className: "hidden",
              }}
            />
            <Typography variant="h6" color="blue-gray" className="-mb-3">
              ราคาเต็ม <span className="text-red-500">*</span>
            </Typography>
            <Input
              type="text"
              size={window.innerWidth < 720 ? "md" : "lg"}
              placeholder={isValid.price ? "ราคา" : "จำเป็นต้องใส่ราคา"}
              name="price"
              value={formatNumberWithCommas(formData.price)}
              onChange={handlePriceChange}
              onKeyDown={onKeyDownNumber}
              className={` ${
                isValid.price
                  ? "text-blue-gray-700 !border-t-blue-gray-200 placeholder:text-blue-gray-700 placeholder:opacity-100 placeholder-shown:border placeholder-shown:border-blue-gray-200 placeholder-shown:border-t-blue-gray-200 focus:!border-2 focus:border-t-transparent focus:placeholder:opacity-100 focus:!border-gray-900 focus:!border-t-gray-900"
                  : "!border-red-500 text-red-500 placeholder:text-red-500 placeholder:opacity-100 placeholder-shown:border placeholder-shown:border-red-500 placeholder-shown:border-t-red-500 focus:!border-2 border-t-transparent focus:border-t-transparent focus:placeholder:opacity-100 focus:!border-red-900 focus:!border-t-red-900"
              } bg-white/70 appearance-none [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
              containerProps={{ className: "min-w-0" }}
              labelProps={{
                className: "hidden",
              }}
              error={!isValid.price}
            />
            <Typography variant="h6" color="blue-gray" className="-mb-3">
              ราคาที่ลดแล้ว
            </Typography>
            <Input
              type="text"
              size={window.innerWidth < 720 ? "md" : "lg"}
              placeholder="ราคาที่ลดแล้ว"
              name="finalPrice"
              value={formatNumberWithCommas(formData.finalPrice)}
              onChange={handlePriceChange}
              onKeyDown={onKeyDownNumber}
              className="bg-white/70 appearance-none [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none text-blue-gray-700 !border-t-blue-gray-200 placeholder:text-blue-gray-700 placeholder:opacity-100 placeholder-shown:border placeholder-shown:border-blue-gray-200 placeholder-shown:border-t-blue-gray-200 focus:!border-2 focus:border-t-transparent focus:placeholder:opacity-100 focus:!border-gray-900 focus:!border-t-gray-900"
              labelProps={{ className: "hidden" }}
              containerProps={{ className: "min-w-0" }}
            />
          </div>
        </div>
        <div className="flex flex-col gap-6">
          <div className="flex flex-col mb-6 w-full">
            <Typography variant="h6" color="blue-gray" className="mb-3">
              แท็ก
            </Typography>
            <MultiSelect
              name="แท็ก"
              options={options}
              value={formData.tags}
              onChange={(newTags) =>
                setFormData((prevData) => ({
                  ...prevData,
                  tags: newTags,
                }))
              }
            />
          </div>
          <div className="flex flex-col mb-6 gap-y-12 w-full xl:w-4/6">
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-3">
                <Typography variant="h6" color="blue-gray">
                  ขนาดพื้นที่ (ตารางวา)
                </Typography>
                <div>
                  <Input
                    type="text"
                    size={window.innerWidth < 720 ? "md" : "lg"}
                    placeholder="ตารางวา"
                    name="areaSquareWah"
                    value={
                      formData.areaSquareWah === 0 ? "" : formData.areaSquareWah
                    }
                    onChange={handleNumberChange}
                    onKeyDown={onKeyDownNumber}
                    className={` ${
                      isValid.areaSquareWah
                        ? "text-blue-gray-700 !border-t-blue-gray-200 placeholder:text-blue-gray-700 placeholder:opacity-100 placeholder-shown:border placeholder-shown:border-blue-gray-200 placeholder-shown:border-t-blue-gray-200 focus:!border-2 focus:border-t-transparent focus:placeholder:opacity-100 focus:!border-gray-900 focus:!border-t-gray-900"
                        : "!border-red-500 text-red-500 placeholder:text-red-500 placeholder:opacity-100 placeholder-shown:border placeholder-shown:border-red-500 placeholder-shown:border-t-red-500 focus:!border-2 border-t-transparent focus:border-t-transparent focus:placeholder:opacity-100 focus:!border-red-900 focus:!border-t-red-900"
                    } bg-white/70 appearance-none [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
                    containerProps={{ className: "min-w-0" }}
                    labelProps={{
                      className: "hidden",
                    }}
                    error={!isValid.areaSquareWah}
                  />
                  {!isValid.areaSquareWah && (
                    <Typography
                      variant="small"
                      color="red"
                      className="mt-2 flex items-center gap-1 font-normal"
                    >
                      ตารางวา ต้องไม่เกิน 400
                    </Typography>
                  )}
                </div>
              </div>
              <div className="flex flex-col gap-3">
                <Typography variant="h6" color="blue-gray">
                  ขนาดพื้นที่ (ไร่)
                </Typography>
                <Input
                  min={0}
                  type="text"
                  size={window.innerWidth < 720 ? "md" : "lg"}
                  placeholder="ไร่"
                  name="areaRai"
                  value={formData.areaRai === 0 ? "" : formData.areaRai}
                  onChange={handleNumberChange}
                  onKeyDown={onKeyDownNumber}
                  className="bg-white/70 appearance-none [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none text-blue-gray-700 !border-t-blue-gray-200 placeholder:text-blue-gray-700 placeholder:opacity-100 placeholder-shown:border placeholder-shown:border-blue-gray-200 placeholder-shown:border-t-blue-gray-200 focus:!border-2 focus:border-t-transparent focus:placeholder:opacity-100 focus:!border-gray-900 focus:!border-t-gray-900"
                  containerProps={{ className: "min-w-0" }}
                  labelProps={{
                    className: "hidden",
                  }}
                />
              </div>
              <Typography variant="h6" color="blue-gray">
                รายละเอียด
              </Typography>
              <Editor
                editAble={true}
                editorState={description}
                setEditorState={setDescription}
              />
            </div>
            <div className="flex flex-col gap-3">
              <Typography variant="h6" color="blue-gray">
                สถานที่ใกล้เคียง
              </Typography>
              <Editor
                editAble={true}
                editorState={nearLocation}
                setEditorState={setNearLocation}
              />
            </div>
            <div className="flex flex-col gap-3">
              <Typography variant="h6" color="blue-gray">
                ต้นไม้ในที่ดิน
              </Typography>
              <Editor
                editAble={true}
                editorState={treeList}
                setEditorState={setTreeList}
              />
            </div>
          </div>
        </div>
        <Button
          className="mt-40"
          onClick={() => setIsModalOpen(true)}
          fullWidth
        >
          สร้างที่ดินใหม่
        </Button>
        <Dialog open={isModalOpen} handler={setIsModalOpen}>
          <DialogHeader>ยืนยัน</DialogHeader>
          <DialogBody>ตรวจสอบข้อมูลให้ถูกต้องก่อนสร้างที่ดินใหม่</DialogBody>
          <DialogFooter>
            <Button onClick={() => setIsModalOpen(false)} className="mr-2">
              ยกเลิก
            </Button>
            <Button
              variant="gradient"
              color="green"
              disabled={loading}
              onClick={handleSubmit}
            >
              {!loading ? "ยืนยัน" : "กำลังสร้างที่ดินใหม่..."}
            </Button>
          </DialogFooter>
        </Dialog>
        <Dialog open={isResultModalOpen} handler={setIsResultModalOpen}>
          <DialogBody className="flex flex-col justify-center">
            <Typography
              variant="h6"
              color="blue-gray"
              className="mb-3 text-2xl"
            >
              ผลลัพธ์
            </Typography>
            <Typography
              variant="h6"
              className="ml-2 mb-3 !text-h1 text-blue-gray-800"
            >
              {propertyError || error ? propertyError ?? error : "สำเร็จ"}
            </Typography>
          </DialogBody>
          <DialogFooter>
            <Button
              onClick={() => {
                setIsResultModalOpen(false);
                console.log("propertyError", propertyError, "error", error);
                if (propertyError === null && error === "") {
                  navigate("/properties");
                }
              }}
              className="mr-2"
            >
              ตกลง
            </Button>
          </DialogFooter>
        </Dialog>
      </form>
    </div>
  );
};

export default CreatePropertyForm;
